import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSessionJwt } from '../hooks/useSessionJwt';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { v2ServicePath } from '../config';

const LIMIT = 50;

// ── normalizers ────────────────────────────────────────────────────────────

export function normalizeV2Trace(t) {
  const inputTokens  = t.total_input_tokens  ?? 0;
  const outputTokens = t.total_output_tokens ?? 0;
  const wf = t.workflow ?? {};
  return {
    trace_id:              t.trace_id,
    trace_start:           t.start_time ? String(t.start_time) : null,
    session_id:            t.session_id ?? null,
    user_id:               t.user_id ?? null,
    duration_ms:           t.duration_ms ?? 0,
    span_count:            t.span_count ?? 0,
    errors:                t.error_count ?? 0,
    tokens:                inputTokens + outputTokens,
    cost:                  t.cost ?? 0,
    agents:                Array.isArray(t.agent_names) ? t.agent_names : [],
    models:                Array.isArray(t.model_names) ? t.model_names : [],
    tools:                 Array.isArray(t.tool_names)  ? t.tool_names  : [],
    input:                 t.first_user_input      ?? null,
    output:                t.last_assistant_output ?? null,
    workflow_name:         t.workflow_name ?? null,
    workflow: {
      workflow_name:    t.workflow_name ?? null,
      outcome:          wf.outcome         ?? null,
    },
    has_prompt_injection:  Boolean(t.has_prompt_injection),
    has_data_exfiltration: Boolean(t.has_data_exfiltration),
    tool_status:           t.tool_status ?? null,
    signals:               Array.isArray(t.signals)    ? t.signals    : [],
    detections:            Array.isArray(t.detections) ? t.detections : [],
    quality:               t.quality ?? null,
  };
}

export function normalizeV2ClassifierTrace(t) {
  const trace = normalizeV2Trace(t);
  const detection = {
    classifier_key: t.classifier_key,
    classifier_version: t.classifier_version,
    detector_version: t.detector_version,
    status: t.status,
    detected: Boolean(t.detected),
    severity: t.severity,
    primary_subtype: t.primary_subtype || '',
    primary_event_key: t.primary_event_key || '',
    reason: t.reason,
    evidence_span_ids: Array.isArray(t.evidence_span_ids) ? t.evidence_span_ids : [],
    primary_span_id: t.primary_span_id,
    evidence: t.evidence,
    details: t.details || {},
  };
  return {
    ...trace,
    ...detection,
    detections: [detection],
  };
}

export function normalizeV1Trace(t) {
  const agents = Array.isArray(t.agents) ? t.agents : (t.agent ? [t.agent] : []);
  const models = Array.isArray(t.models) ? t.models : (t.model ? [t.model] : []);
  const tools = Array.isArray(t.tools) ? t.tools : (t.tool ? [t.tool] : []);
  return {
    trace_id: t.trace_id,
    trace_start: t.trace_start || t.start_time || null,
    session_id: t.session_id ?? null,
    user_id: t.user_id ?? null,
    duration_ms: t.duration_ms ?? t.latency_ms ?? 0,
    span_count: t.span_count ?? 0,
    errors: t.errors ?? t.error_count ?? 0,
    tokens: t.tokens ?? ((t.input_tokens ?? 0) + (t.output_tokens ?? 0)),
    cost: t.cost ?? 0,
    agents,
    models,
    tools,
    input: t.input ?? t.first_user_input ?? null,
    output: t.output ?? t.last_assistant_output ?? null,
    workflow_name: t.workflow_name ?? t.workflow?.workflow_name ?? null,
    workflow: t.workflow || {},
    has_prompt_injection: Boolean(t.has_prompt_injection),
    has_data_exfiltration: Boolean(t.has_data_exfiltration),
    tool_status: t.tool_status ?? null,
    signals: Array.isArray(t.signals) ? t.signals : [],
    detections: Array.isArray(t.detections) ? t.detections : [],
    quality: t.quality ?? null,
  };
}

// ── hook ───────────────────────────────────────────────────────────────────

/**
 * Fetches the trace list for a service with filtering and pagination.
 *
 * filters keys: sessionId, agent, tool, model, workflow, workflowId, outcome,
 *               problemId, startTime, endTime, minLatency, maxLatency
 *
 * signalGroup/signal/sort are read on the Traces page but not forwarded here —
 * v2 list_traces has no server-side filter for them.
 *
 * Returns { traces, loading, error, hasMore, page, loadNextPage, loadPrevPage,
 *           entities, info }
 *   traces — null while loading, [] when empty, array when populated
 */
export function useTraceList(selectedAppId, filters = {}, { enabled = true } = {}) {
  const { selectedRange } = useTimeRange();
  const { selectedEnvironment } = useEnvironment();
  const { sessionId, getJwt } = useSessionJwt();

  const [traces, setTraces]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [hasMore, setHasMore]       = useState(false);
  const [entities, setEntities]     = useState({ agents: [], tools: [], models: [], workflows: [] });
  const [info, setInfo]             = useState(null);
  const [page, setPage]             = useState(1);

  // Cursor stack for v2 pagination. Index i holds the cursor to request page i+1.
  // cursorStack[0] = null (no cursor for page 1).
  // Stored in a ref so updating it doesn't cause re-renders / extra fetches.
  const cursorStackRef = useRef([null]);

  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);

  // Reset pagination whenever filters or time context changes.
  useEffect(() => {
    setPage(1);
    cursorStackRef.current = [null];
    setTraces(null);
    setLoading(true);  // Set loading immediately when filters change
  }, [filterKey, selectedRange, selectedEnvironment]);

  useEffect(() => {
    const jwt = getJwt();
    if (!enabled || !jwt || !selectedAppId) {
      setLoading(false);
      return;
    }

    const abort = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const qs = new URLSearchParams();

        if (selectedEnvironment)  qs.set('env',         selectedEnvironment);
        if (selectedRange)        qs.set('range',        selectedRange);
        qs.set('limit', String(LIMIT));
        const cursor = cursorStackRef.current[page - 1] ?? null;
        if (cursor)               qs.set('cursor',       cursor);
        if (filters.sessionId)       qs.set('session_id',      filters.sessionId);
        if (filters.userId)          qs.set('user_id',         filters.userId);
        if (filters.agent)           qs.set('agent',           filters.agent);
        if (filters.tool)            qs.set('tool',            filters.tool);
        if (filters.model)           qs.set('model',           filters.model);
        if (filters.workflow)        qs.set('workflow',        filters.workflow);
        if (filters.workflowId)      qs.set('workflow_id',     filters.workflowId);
        if (filters.outcome)         qs.set('outcome',         filters.outcome);
        if (filters.problemId)        qs.set('problem_id',      filters.problemId);
        if (filters.startTime)       qs.set('start_time',      filters.startTime);
        if (filters.endTime)         qs.set('end_time',        filters.endTime);
        if (filters.minLatency)      qs.set('min_latency',     filters.minLatency);
        if (filters.maxLatency)      qs.set('max_latency',     filters.maxLatency);

        const url = `${apiUrl}${v2ServicePath(selectedAppId, '/traces')}?${qs}`;
        const resp = await fetch(url, {
          signal: abort.signal,
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const raw = await resp.json();

        const rows = Array.isArray(raw.data) ? raw.data : [];
        const norm = rows.map(normalizeV2Trace);
        setTraces(norm);
        setHasMore(raw.meta?.has_more ?? false);
        setInfo(null);


        if (raw.meta?.next_cursor) {
          cursorStackRef.current[page] = raw.meta.next_cursor;
        }

        const agentSet = new Set(), toolSet = new Set(), modelSet = new Set(), wfSet = new Set();
        norm.forEach(t => {
          t.agents.forEach(a => agentSet.add(a));
          t.tools.forEach(a => toolSet.add(a));
          t.models.forEach(a => modelSet.add(a));
          if (t.workflow_name) wfSet.add(t.workflow_name);
        });
        setEntities({
          agents:    [...agentSet].sort(),
          tools:     [...toolSet].sort(),
          models:    [...modelSet].sort(),
          workflows: [...wfSet].sort(),
        });
      } catch (e) {
        if (e.name !== 'AbortError') {
          setError(e.message);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => abort.abort();
  }, [enabled, sessionId, getJwt, selectedAppId, selectedEnvironment, selectedRange, filterKey, page]);

  const loadNextPage = useCallback(() => {
    if (hasMore && !loading) setPage(p => p + 1);
  }, [hasMore, loading]);

  const loadPrevPage = useCallback(() => {
    if (page > 1 && !loading) setPage(p => p - 1);
  }, [page, loading]);

  return { traces, loading, error, hasMore, page, loadNextPage, loadPrevPage, entities, info };
}
